import {
  ERPPostingStatus,
  FlexCodeTransactionStatus,
  HighnoteTransactionEvent,
  ReturnedStatus,
  WorkflowStatus,
} from 'API';
import { isTransactionFinalized, shouldShowNeedsReviewStatus } from 'app/services/agave/erpUtils';

export type AccountType = 'connected' | 'flex';

export type StatusKey =
  | 'new'
  | 'needsReview'
  | 'returned'
  | 'groupApproval'
  | 'groupApproval1'
  | 'groupFinalApproval'
  | 'pendingApproval'
  | 'pendingApproval1'
  | 'pendingFinalApproval'
  | 'approved'
  | 'posting'
  | 'posted'
  | 'bypassed'
  | 'exported';

/**
 * What a chip says, which is not always the step it sits on: on the default
 * workflow both account types call that step Reviewed. The chip takes its colour
 * from this rather than from StatusKey, so the wording and the treatment agree.
 */
export type LabelKey = StatusKey | 'reviewed' | 'sageImportError';

/** Connected reviews straight to posting; flex has one approval step. */
export const DEFAULT_APPROVAL_LEVELS: Record<AccountType, number> = {
  connected: 0,
  flex: 1,
};

// The clamp only ever moves forward along this order, so a transaction can never
// be stranded on a step its account no longer has, nor sent backwards.
const CHAIN_ORDER: WorkflowStatus[] = [
  WorkflowStatus.NEW,
  WorkflowStatus.NEEDS_REVIEW,
  WorkflowStatus.GROUP_APPROVAL_1,
  WorkflowStatus.GROUP_FINAL_APPROVAL,
  WorkflowStatus.PENDING_APPROVAL_1,
  WorkflowStatus.PENDING_FINAL_APPROVAL,
  WorkflowStatus.APPROVED,
];

/**
 * The steps this transaction actually has, in order.
 *
 * Both account types share one chain — connected and flex end on the same
 * APPROVED — so only the configured levels shape it.
 *
 * `groupLevels` are the approval levels on the group owning the transaction.
 * They are additive to the company's and sit ahead of them, which is why the
 * chain is per transaction rather than per company. Defaults to zero so a caller
 * with no group in hand keeps the company-only chain.
 */
export const chainFor = (approvalLevels: number, groupLevels = 0): WorkflowStatus[] => {
  const chain = [WorkflowStatus.NEW, WorkflowStatus.NEEDS_REVIEW];
  if (groupLevels >= 2) chain.push(WorkflowStatus.GROUP_APPROVAL_1);
  if (groupLevels >= 1) chain.push(WorkflowStatus.GROUP_FINAL_APPROVAL);
  if (approvalLevels >= 2) chain.push(WorkflowStatus.PENDING_APPROVAL_1);
  if (approvalLevels >= 1) chain.push(WorkflowStatus.PENDING_FINAL_APPROVAL);
  chain.push(WorkflowStatus.APPROVED);
  return chain;
};

/**
 * Translation key for a status.
 *
 * On the default workflow both account types keep production's wording: the step
 * a review lands on is called Reviewed. Only the label changes — connected stores
 * APPROVED there, flex stores PENDING_FINAL_APPROVAL. Once an account adds an
 * approval step, the steps are named for what they await.
 */
export const labelKeyFor = (
  status: StatusKey,
  accountType: AccountType,
  approvalLevels: number,
  groupApprovalsEnabled = false
): LabelKey => {
  // Only where the review is the last word. An account routing through group
  // approvers names the step for what it awaits, account-wide — a row whose own
  // group adds no step is still on an account that can, and two rows at the same
  // step must not read differently because of the group their card is in.
  if (groupApprovalsEnabled) return status;
  if (approvalLevels !== DEFAULT_APPROVAL_LEVELS[accountType]) return status;
  const reviewedKey = accountType === 'connected' ? 'approved' : 'pendingApproval';
  return status === reviewedKey ? 'reviewed' : status;
};

/** The nearest step still in the chain, or null if the value isn't a chain step. */
export const clampStatus = (status: WorkflowStatus, chain: WorkflowStatus[]): WorkflowStatus | null => {
  if (chain.includes(status)) return status;
  const from = CHAIN_ORDER.indexOf(status);
  if (from < 0) return null;
  return CHAIN_ORDER.slice(from).find((step) => chain.includes(step)) ?? null;
};

const keyForStatus = (status: WorkflowStatus, approvalLevels: number, groupLevels = 0): StatusKey | null => {
  switch (status) {
    case WorkflowStatus.NEW:
      return 'new';
    case WorkflowStatus.NEEDS_REVIEW:
      return 'needsReview';
    case WorkflowStatus.GROUP_APPROVAL_1:
      return 'groupApproval1';
    // The last group step is named for how many levels the group has, exactly as
    // the last company step is named for how many the company has.
    case WorkflowStatus.GROUP_FINAL_APPROVAL:
      return groupLevels >= 2 ? 'groupFinalApproval' : 'groupApproval';
    case WorkflowStatus.PENDING_APPROVAL_1:
      return 'pendingApproval1';
    case WorkflowStatus.PENDING_FINAL_APPROVAL:
      return approvalLevels >= 2 ? 'pendingFinalApproval' : 'pendingApproval';
    case WorkflowStatus.APPROVED:
      return 'approved';
    default:
      return null;
  }
};

// How far along the chain a key sits. Ranks 0 and 1 are indistinguishable to the
// legacy derivation, which has no way to express New.
const RANK: Record<StatusKey, number> = {
  new: 0,
  needsReview: 1,
  returned: 1,
  groupApproval1: 2,
  groupApproval: 3,
  groupFinalApproval: 3,
  pendingApproval1: 4,
  pendingApproval: 5,
  pendingFinalApproval: 5,
  approved: 6,
  posting: 7,
  posted: 7,
  bypassed: 7,
  exported: 7,
};

// Posting supersedes workflow position, which is what stops a posted row from
// still reading Returned.
const finalizingOutcome = (
  transaction: HighnoteTransactionEvent,
  isTransactionPosting?: boolean
): StatusKey | null => {
  // SEE-1294: flex's terminal outcome, the analog of Posted. Like the posting
  // outcomes below it supersedes the workflow position rather than sitting on the
  // chain, so an exported row never reads as the Approved it still stores.
  if (transaction.workflowStatus != null && transaction.workflowStatus === WorkflowStatus?.EXPORTED) {
    return 'exported';
  }
  const postingStatus = transaction.erpPostingStatus;
  if (
    postingStatus != null &&
    (postingStatus === ERPPostingStatus?.PENDING || postingStatus === ERPPostingStatus?.PROCESSING)
  ) {
    return 'posting';
  }
  if (isTransactionPosting) return 'posting';
  if (transaction.erpBypassStatus) return 'bypassed';
  if (
    transaction.erpApprovalStatus &&
    (transaction.agaveExpenseId || (postingStatus != null && postingStatus === ERPPostingStatus?.COMPLETED))
  ) {
    return 'posted';
  }
  return null;
};

const isReturned = (transaction: HighnoteTransactionEvent): boolean =>
  (transaction.workflowStatus != null && transaction.workflowStatus === WorkflowStatus?.RETURNED) ||
  (transaction.returnedStatus != null &&
    transaction.returnedStatus === ReturnedStatus?.RETURNED &&
    !isTransactionFinalized(transaction));

// Where a reviewed transaction sits, by how many approval steps the account has.
// Mirrors _REVIEWED_DESTINATION in the CDK's update_transaction.
export const REVIEWED_DESTINATION: Record<number, WorkflowStatus> = {
  0: WorkflowStatus.APPROVED,
  1: WorkflowStatus.PENDING_FINAL_APPROVAL,
  2: WorkflowStatus.PENDING_APPROVAL_1,
};

/**
 * The step a completed review lands on.
 *
 * A group's approvers come before the company's, so any group level takes the
 * transaction first and REVIEWED_DESTINATION only applies once there are none.
 */
export const destinationAfterReview = (approvalLevels: number, groupLevels = 0): WorkflowStatus => {
  if (groupLevels >= 2) return WorkflowStatus.GROUP_APPROVAL_1;
  if (groupLevels >= 1) return WorkflowStatus.GROUP_FINAL_APPROVAL;
  return REVIEWED_DESTINATION[approvalLevels];
};

const normalizeLevels = (approvalLevels: number): number =>
  Math.min(2, Math.max(0, Math.trunc(approvalLevels)));

export const chainIndexOf = (status: WorkflowStatus): number => CHAIN_ORDER.indexOf(status);

/**
 * The statuses a bulk action can move a selection to. Empty on both default
 * workflows. APPROVED is dropped on flex because its approve is a distinct action
 * that also writes flexCodeTransactionStatus and the auto-lock exemption flags.
 */
export const bulkTargetsFor = (accountType: AccountType, approvalLevels: number): WorkflowStatus[] => {
  const levels = normalizeLevels(approvalLevels);
  const afterReview = chainIndexOf(REVIEWED_DESTINATION[levels]);
  return chainFor(levels).filter(
    (step) =>
      chainIndexOf(step) > afterReview && !(accountType === 'flex' && step === WorkflowStatus.APPROVED)
  );
};

/** Group steps, which a group's own approvers clear before the company's. */
export const GROUP_STEP_KEYS: StatusKey[] = ['groupApproval', 'groupApproval1', 'groupFinalApproval'];

/**
 * The group level whose approver may clear this step, or null if it is not a
 * group step. Mirrors _level_that_clears in the CDK's update_transaction: the
 * last group step is always the final one, so at one level it belongs to the
 * level 1 approver and at two to the level 2 approver.
 */
export const levelThatClears = (status: StatusKey | null, groupLevels: number): number | null => {
  if (status === 'groupApproval1') return 1;
  if (status === 'groupApproval' || status === 'groupFinalApproval') return groupLevels || 1;
  return null;
};

/** The step after this one in the transaction's own chain, or null at the end. */
export const nextStepAfter = (
  status: WorkflowStatus,
  approvalLevels: number,
  groupLevels = 0
): WorkflowStatus | null => {
  const chain = chainFor(approvalLevels, groupLevels);
  const index = chain.indexOf(status);
  return index >= 0 && index < chain.length - 1 ? chain[index + 1] : null;
};

/** The stored step a display key corresponds to, for surfaces acting on a row. */
export const storedStepFor = (status: StatusKey | null): WorkflowStatus | null => {
  if (status === 'groupApproval1') return WorkflowStatus.GROUP_APPROVAL_1;
  if (status === 'groupApproval' || status === 'groupFinalApproval')
    return WorkflowStatus.GROUP_FINAL_APPROVAL;
  return null;
};

/** Label key for a chain step, for surfaces naming a status they are moving to. */
export const labelKeyForStatus = (
  status: WorkflowStatus,
  accountType: AccountType,
  approvalLevels: number,
  groupLevels = 0,
  groupApprovalsEnabled = false
): LabelKey | null => {
  const key = keyForStatus(status, approvalLevels, groupLevels);
  return key ? labelKeyFor(key, accountType, approvalLevels, groupApprovalsEnabled) : null;
};

const legacyStatus = (
  transaction: HighnoteTransactionEvent,
  accountType: AccountType,
  approvalLevels: number,
  groupLevels = 0
): StatusKey | null => {
  // The legacy flags only ever say "reviewed", and they predate workflowStatus —
  // so they can never have routed a row through a group step. Their destination
  // is the company's, whatever the row's group staffs now: a historical reviewed
  // row keeps the position it always had, and only the write path puts rows on
  // group steps.
  const reviewed =
    keyForStatus(REVIEWED_DESTINATION[approvalLevels], approvalLevels, groupLevels) ?? 'approved';

  if (accountType === 'flex') {
    const flexStatus = transaction.flexCodeTransactionStatus;
    if (flexStatus != null && flexStatus === FlexCodeTransactionStatus?.REVIEWED) {
      return reviewed;
    }
    if (flexStatus != null && flexStatus === FlexCodeTransactionStatus?.APPROVED) {
      return 'approved';
    }
    return 'needsReview';
  }
  if (transaction.erpApprovalStatus) return reviewed;
  return shouldShowNeedsReviewStatus(transaction) ? 'needsReview' : null;
};

export type ResolveStatusArgs = {
  transaction?: HighnoteTransactionEvent | null;
  accountType: AccountType;
  approvalLevels?: number;
  /**
   * Approval levels on the group owning this transaction, resolved server-side
   * from the group its card is in now. Zero when the card has no group, or when
   * group approvals are off.
   */
  groupApprovalLevels?: number;
  isTransactionPosting?: boolean;
};

/**
 * Where a transaction sits, as a display key.
 *
 * Prefers the stored workflowStatus and falls back to the legacy per-type
 * derivation, so rows written before the column existed are unaffected.
 */
export const resolveStatus = ({
  transaction,
  accountType,
  approvalLevels,
  groupApprovalLevels,
  isTransactionPosting,
}: ResolveStatusArgs): StatusKey | null => {
  if (!transaction) return null;
  const levels = approvalLevels ?? DEFAULT_APPROVAL_LEVELS[accountType];
  const groupLevels = groupApprovalLevels ?? transaction.groupApprovalLevels ?? 0;

  const outcome = finalizingOutcome(transaction, isTransactionPosting);
  if (outcome) return outcome;

  if (isReturned(transaction)) return 'returned';

  const legacy = legacyStatus(transaction, accountType, levels, groupLevels);

  const stored = transaction.workflowStatus;
  const clamped = stored ? clampStatus(stored, chainFor(levels, groupLevels)) : null;
  // An unrecognized value falls through to the legacy derivation rather than
  // rendering a wrong status.
  let key = clamped ? keyForStatus(clamped, levels, groupLevels) : null;

  // A row on a group step was reviewed, so the legacy field says so too and would
  // outrank the group step it is actually waiting on. The legacy derivation only
  // exists to rescue rows written before workflowStatus, and such a row can never
  // hold a group step, so it has nothing to say about one.
  const isGroupStep = key != null && GROUP_STEP_KEYS.includes(key);

  // A row reviewed before the column started being written still carries the
  // marker it was ingested with, so a legacy field that says reviewed or beyond
  // wins over a stale New / Needs Review. Ranks 0-1 can't be told apart by the
  // legacy derivation, so they never win.
  if (key && legacy && !isGroupStep && RANK[legacy] >= 2 && RANK[legacy] > RANK[key]) {
    key = legacy;
  }

  if (!key) return legacy;

  const isPreReview = key === 'new' || key === 'needsReview';
  if (isPreReview && accountType === 'connected' && !shouldShowNeedsReviewStatus(transaction)) {
    return null;
  }
  return key;
};

// Returned is -1, not alongside Needs review: a returned transaction is actionable
// like one awaiting review, matching how the CDK reads it.
const CHAIN_POSITION: Record<StatusKey, number> = {
  new: chainIndexOf(WorkflowStatus.NEW),
  needsReview: chainIndexOf(WorkflowStatus.NEEDS_REVIEW),
  returned: -1,
  groupApproval1: chainIndexOf(WorkflowStatus.GROUP_APPROVAL_1),
  groupApproval: chainIndexOf(WorkflowStatus.GROUP_FINAL_APPROVAL),
  groupFinalApproval: chainIndexOf(WorkflowStatus.GROUP_FINAL_APPROVAL),
  pendingApproval1: chainIndexOf(WorkflowStatus.PENDING_APPROVAL_1),
  pendingApproval: chainIndexOf(WorkflowStatus.PENDING_FINAL_APPROVAL),
  pendingFinalApproval: chainIndexOf(WorkflowStatus.PENDING_FINAL_APPROVAL),
  approved: chainIndexOf(WorkflowStatus.APPROVED),
  posting: Number.POSITIVE_INFINITY,
  posted: Number.POSITIVE_INFINITY,
  bypassed: Number.POSITIVE_INFINITY,
  exported: Number.POSITIVE_INFINITY,
};

/**
 * How far along the chain a transaction sits. Infinity once posting has made it
 * terminal, so `position < chainIndexOf(target)` alone decides bulk eligibility.
 */
export const chainPositionOf = (args: ResolveStatusArgs): number => {
  const key = resolveStatus(args);
  return key ? CHAIN_POSITION[key] : -1;
};
